import type { HttpEvent } from '@angular/common/http';
import { HttpResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs/operators';

import type { FieldOf } from '../../core/api/adapters';
import { RecentlyViewedService } from '../../dashboard/services/recently-viewed.service';
import type { IIssue, IMonitoringTopic, IProcess, IProject, ITableItem, ITask } from '../../project/models';
import type { IReport } from '../../reports/models/report';
import type { IRecentlyViewed } from '../models';
import { apiUrlToId } from '../utils/api-url-to-id.util';

// `entityProp` discriminates, so the two field names each row looks up at runtime are checked
// against that entity's model.
interface IRequestHandlerOption<TEntity, TEntityProp extends string> {
  urlPathRegex: RegExp;
  apiURL: string;
  entityProp: TEntityProp;
  bookmarkProp: FieldOf<TEntity, number | null>;
  titleProp: FieldOf<TEntity, string>;
}

type RequestHandlerOption =
  | IRequestHandlerOption<IMonitoringTopic, 'monitoringtopic'>
  | IRequestHandlerOption<ITableItem, 'table'>
  | IRequestHandlerOption<IIssue, 'issue'>
  | IRequestHandlerOption<IProject, 'project'>
  | IRequestHandlerOption<IProcess, 'process'>
  | IRequestHandlerOption<IReport, 'report'>
  | IRequestHandlerOption<ITask, 'task'>;

type ViewedEntity = Record<string, unknown> & { id: number };

const options: RequestHandlerOption[] = [
  {
    urlPathRegex: new RegExp(/^\/api\/monitoringtopic\/(\d+)\/?$/),
    apiURL: '/api/monitoringtopic',
    entityProp: 'monitoringtopic',
    bookmarkProp: 'monitoringtopic_bookmarked',
    titleProp: 'name',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/table\/(\d+)\/?$/),
    apiURL: '/api/table',
    entityProp: 'table',
    bookmarkProp: 'table_bookmarked',
    titleProp: 'name',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/issue\/(\d+)\/?$/),
    apiURL: '/api/issue?is_ticket=0',
    entityProp: 'issue',
    bookmarkProp: 'issue_bookmarked',
    titleProp: 'title',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/issue\/(\d+)\/?$/),
    apiURL: '/api/issue?is_ticket=1',
    entityProp: 'issue',
    bookmarkProp: 'issue_bookmarked',
    titleProp: 'title',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/project\/(\d+)\/?$/),
    apiURL: '/api/project?detail_serializer=1',
    entityProp: 'project',
    bookmarkProp: 'project_bookmarked',
    titleProp: 'name',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/process\/(\d+)\/?$/),
    apiURL: '/api/process',
    entityProp: 'process',
    bookmarkProp: 'process_bookmarked',
    titleProp: 'title',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/report\/(\d+)\/?$/),
    apiURL: '/api/report',
    entityProp: 'report',
    bookmarkProp: 'report_bookmarked',
    titleProp: 'title',
  },
  {
    urlPathRegex: new RegExp(/^\/api\/task\/(\d+)\/?$/),
    apiURL: '/api/task',
    entityProp: 'task',
    bookmarkProp: 'task_bookmarked',
    titleProp: 'title',
  },
];

const issueURL = (isTicket: boolean) => (isTicket ? '/api/issue?is_ticket=1' : '/api/issue?is_ticket=0');

const getOptionURL = (option: RequestHandlerOption, response: ViewedEntity) =>
  option.entityProp === 'issue' ? issueURL(response.is_ticket === true) : option.apiURL;

// A PUT never moves an issue between the two buckets, so rename in whichever one holds the id.
const updateURLs = (option: RequestHandlerOption): string[] =>
  option.entityProp === 'issue' ? [issueURL(false), issueURL(true)] : [option.apiURL];

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isViewedEntity = (value: unknown): value is ViewedEntity => isRecord(value) && typeof value.id === 'number';

const readString = (source: Record<string, unknown>, prop: string): string | undefined => {
  const value = source[prop];
  return typeof value === 'string' ? value : undefined;
};

const toRecentlyViewed = (body: ViewedEntity, option: RequestHandlerOption, requestUrl: string): IRecentlyViewed =>
  option.entityProp === 'task'
    ? { ...body, title: readString(body, option.titleProp) ?? '', url: readString(body, 'url') ?? requestUrl, entityProp: 'task' }
    : { id: body.id, title: readString(body, option.titleProp) ?? '', url: requestUrl, entityProp: option.entityProp };

export const recentlyViewedInterceptor: HttpInterceptorFn = (request, next) => {
  const recentlyViewedService = inject(RecentlyViewedService);
  const { url } = request;
  const option = options.find(({ urlPathRegex }) => urlPathRegex.test(url));
  if (option) {
    return next(request).pipe(
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse && [200, 204].includes(event.status)) {
          try {
            const { body } = event;

            if (isViewedEntity(body) && request.method === 'GET') {
              if (!body[option.bookmarkProp]) {
                recentlyViewedService.addItem(getOptionURL(option, body), toRecentlyViewed(body, option, url));
              } else {
                recentlyViewedService.removeItem(option.apiURL, body.id);
              }
            }
            // The new title comes from the request: these PUTs may answer 204 with no body.
            const title = request.method === 'PUT' && isRecord(request.body) ? readString(request.body, option.titleProp) : undefined;
            if (title !== undefined) {
              const id = parseInt(apiUrlToId(url) ?? '');
              updateURLs(option).forEach(apiURL => recentlyViewedService.renameItem(apiURL, id, title));
            }
            if (request.method === 'DELETE') {
              const id = apiUrlToId(request.url);
              recentlyViewedService.removeItem(option.apiURL, parseInt(id ?? ''));
            }
          } catch {
            new Error('Cannot manage recently viewed item');
          }
        }
      }),
    );
  }
  return next(request);
};
